import { AnimatePresence } from 'framer-motion';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AddressButton from '@/components/AddressButton';
import { CalendarWithPark } from '@/components/CalendarWithPark';
import { CalendarWithServices } from '@/components/CalendarWithServices';
import { CalendarWithWaves } from '@/components/CalendarWithWaves';
import { gatewayErrorMessages, getErrorMessage, getErrorMessageExact } from '@/helpers/errorMessages';
import { getCartType } from '@/helpers/utils';
import { calculateEffectiveDiscount } from '@/helpers/utils';
import { isCouponApplicableToItem } from '@/helpers/couponValidation';
import { useGetDependentsOwner } from '@/hooks/useGetDependentsOwner';
import { usePostCheckout } from '@/hooks/usePostCheckout';
import { useUserProfile } from '@/hooks/useUserData';
import { useApplyCouponFromUrl } from '@/hooks/useApplyCouponFromUrl';
import gatewayService from '@/services/gateway.service';
import { getHospitalityReservationByToken } from '@/services/hospitality.service';
import { useModalAuthStore } from '@/store/auth/authModalStore';
import { useToken } from '@/store/auth/tokenStore';
import { useTermsStore } from '@/store/checkout/termsStore';
import type { PaymentMethod } from '@/store/payment/paymentModalStore';
import { usePaymentModalStore } from '@/store/payment/paymentModalStore';
import {
    CartServiceType,
    CartValuesServiceStore,
    useShoppingCartStore,
} from '@/store/shoppingCart/shoppingCartStore';
import {
    findDailyWaveLimitViolation,
    getCartDailyWaveUsage,
    getReservationDailyWaveUsage,
    mergeDailyWaveUsages,
    useReservationStore,
} from '@/store/reservation/reservationStore';
import type { Address } from '@/@types/ServiceCheckoutInput';
import {
    getNCardInput,
    getValidityInput,
} from '../ModalPaymentCard/pieces/CardForm/logic';
import { CartSubItem } from './CartItem/CartSubItem';
import { CartSummary } from './CartSummary';
import { CouponInput } from './CouponInput';
import useFormProductLogic from './logic';
import PaymentMethodInputs from './PaymentMethod';
import { RelatedItem, type RelatedItemType } from './RelatedItem';
import {
    CheckoutContainer,
    ItemListCard,
    PageContainer,
    Summary,
} from './styles';
import { CheckoutItemParqueType } from './types';
import { WarnNotice, WarnNoticeSeparator } from '@/components/WarnNotice';
import { CheckoutResponseDTO } from '@/DTO/checkoutResponseDTO';
import Decimal from 'decimal.js';
import { calculateCartCampaignDiscount } from '@/store/shoppingCart/shoppingCartStore';
import {
    canPurchaseStoreDate,
    STORE_RESERVATION_REQUIRED_MESSAGE,
    STORE_RESERVATION_WINDOW_MESSAGE,
} from '@/helpers/storeAccess';

type ModalCallbackType = (value: CartValuesServiceStore | null) => void;

interface CheckoutProduct {
    client: {
        document: string;
        name: string;
    };
    date: string;
    productId: number;
    scheduleId?: number;
    value: number;
    discountValue?: number;
    giftId?: number;
    usedDiscountCoupon?: boolean;
    campaignId?: number;
    usedCampaignRuleId?: number;
    freeSession?: boolean;
    reservationId?: number;
}

interface CheckoutPayload {
    value: number;
    discountValue: number;
    couponCode?: string;
    products: CheckoutProduct[];
    address: Address;
    discountCouponId?: number;
}

interface Props {
    shouldHideRecommendations?: boolean;
    shouldBlockEditing?: boolean;
    isPaymentLink?: boolean;
    forcedCheckoutId?: number;
}

const CHECKOUT_LOCK_TTL_MS = 20_000;

const getCheckoutLockKey = (reservationToken: string) =>
    `surfland:checkout:reservation-lock:${reservationToken}`;

const acquireCheckoutLock = (reservationToken: string): (() => void) | null => {
    if (typeof window === 'undefined') return () => undefined;

    const now = Date.now();
    const lockKey = getCheckoutLockKey(reservationToken);
    const lockRaw = window.localStorage.getItem(lockKey);

    if (lockRaw) {
        try {
            const lockData = JSON.parse(lockRaw) as { expiresAt?: number };
            if (typeof lockData.expiresAt === 'number' && lockData.expiresAt > now) {
                return null;
            }
        } catch {
            // ignora lock mal formatado e segue
        }
    }

    window.localStorage.setItem(
        lockKey,
        JSON.stringify({
            createdAt: now,
            expiresAt: now + CHECKOUT_LOCK_TTL_MS,
        })
    );

    return () => {
        window.localStorage.removeItem(lockKey);
    };
};

export const Checkout: React.FC<Props> = ({
    shouldHideRecommendations,
    shouldBlockEditing,
    isPaymentLink,
    forcedCheckoutId,
}) => {
    const postCheckout = usePostCheckout(isPaymentLink);
    const navigate = useNavigate();

    const [defaultData, setDefaultData] = useState<CartValuesServiceStore>();

    // const { data: dataServices, isPending: isPendingServices } =
    //     useGetShopItemsServices({  });

    const [handleModalWaveClose, setModalWaveCloseHandler] =
        useState<ModalCallbackType>();
    const [handleModalServiceClose, setModalServiceCloseHandler] =
        useState<ModalCallbackType>();
    const [handleModalParkClose, setModalParkCloseHandler] =
        useState<ModalCallbackType>();

    const isCalendarServiceOpen =
        Boolean(handleModalServiceClose) && !!defaultData;
    const isCalendarWaveOpen = Boolean(handleModalWaveClose) && !!defaultData;
    const isCalendarParkOpen = Boolean(handleModalParkClose) && !!defaultData;

    const {
        cartValues,
        checkoutId,
        editCartValue,
        setCurrentIdRemoving,
        setCheckoutId,
        address,
    } = useShoppingCartStore();
    const coupon = useShoppingCartStore(state => state.coupon);
    const { setIsVisibleAuthModal } = useModalAuthStore();
    const { regulation, setRegulation } = useTermsStore();
    const { token } = useToken();
    const { activeReservation, setActiveReservation } = useReservationStore();
    const isEditLocked = Boolean(shouldBlockEditing || activeReservation?.token);

    const isCalendarServiceVisible = !isEditLocked && isCalendarServiceOpen;
    const isCalendarWaveVisible = !isEditLocked && isCalendarWaveOpen;
    const isCalendarParkVisible = !isEditLocked && isCalendarParkOpen;

    const userData = useUserProfile().data;

    // Só chama o hook se userData e userData.accessProfile existirem
    const { dependents } = useGetDependentsOwner(
        userData && userData.accessProfile ? userData : undefined
    );

    // Hook para aplicar cupom via URL
    useApplyCouponFromUrl();

    const sumDiscountValue = useMemo(() => {
            if (!cartValues) return 0;
    
            return cartValues.reduce(
                (accumulator, item) => accumulator + (item.discountValue ?? 0),
                0
            );
        }, [cartValues])

    const paymentModal = usePaymentModalStore();    
    const shoppingCartTotalValue = useShoppingCartStore(state => state.cartValues)?.reduce(
        (acc, product) => acc + product.value,
        0
    ) ?? 0

    // Calcular desconto efetivo para obter valor líquido
    const effectiveDiscount = calculateEffectiveDiscount(coupon, shoppingCartTotalValue);

    const finalValue = coupon 
        ? shoppingCartTotalValue - effectiveDiscount
        : shoppingCartTotalValue - sumDiscountValue

    const priceCents = new Decimal(finalValue)
        .mul(Decimal(100))
        .toNumber();

    const isCortesia = useShoppingCartStore(state => {
        if (!state.cartValues) return false;

        const totalValue = shoppingCartTotalValue;
        return state.cartValues.length > 0 && totalValue === 0;
    });

    const cardCredentials = paymentModal.card;
    const cardInstallments = paymentModal.installments;
    const intervalRef = useRef<NodeJS.Timeout>();

    const { errors, register, trigger, setValue, getValues, reset } = useFormProductLogic();

    useEffect(() => {
        reset();
        const formValues = getValues();
        cartValues?.forEach((carValue, index) => {
            const product = formValues.products[index];
            const document = carValue.client?.document || product?.document;
            const name = carValue.client?.name || product?.name;
            const foreigner = carValue.client?.foreigner || product?.foreigner;

            if (!document || !name || foreigner === undefined) return;

            setValue(`products.${index}`, {
                document,
                foreigner,
                name,
            });
        });
        if (cartValues && cartValues.length > 0) {
            trigger('products');
        }
    }, [cartValues, getValues, setValue, trigger, reset]);

    useEffect(() => {
        // Evitar loop infinito: só atualiza o store se o valor atual for diferente
        const currentPurchaseTotal = usePaymentModalStore.getState().purchaseTotal;
        if (currentPurchaseTotal !== shoppingCartTotalValue) {
            usePaymentModalStore.getState().update({ purchaseTotal: shoppingCartTotalValue });
        }
    }, [shoppingCartTotalValue]);

    const handleOpenCalendar = (itemId: string) => {
        if (isEditLocked) return;
        const item = cartValues?.find(i => i.idCart === itemId);

        if (!item) return;
        setDefaultData(item);

        if (item.type === CartServiceType.Waves) {
            setModalWaveCloseHandler(
                () => (closeData: CartValuesServiceStore | null) => {
                    if (!closeData) {
                        setModalWaveCloseHandler(undefined);
                        return;
                    }
                    editCartValue(itemId, closeData);
                    setModalWaveCloseHandler(undefined);
                    calculateCartCampaignDiscount(userData?.accessProfile.name);
                }
            );
        } else if (item.type === CartServiceType.Services) {
            setModalServiceCloseHandler(
                () => (closeData: CartValuesServiceStore | null) => {
                    if (!closeData) {
                        setModalServiceCloseHandler(undefined);
                        return;
                    }
                    editCartValue(itemId, closeData);
                    setModalServiceCloseHandler(undefined);
                    calculateCartCampaignDiscount(userData?.accessProfile.name);
                }
            );
        } else if (item.type === CartServiceType.Park) {
            setModalParkCloseHandler(
                () => (closeData: CartValuesServiceStore | null) => {
                    if (!closeData) {
                        setModalParkCloseHandler(undefined);
                        return;
                    }
                    editCartValue(itemId, closeData);
                    setModalParkCloseHandler(undefined);
                    calculateCartCampaignDiscount(userData?.accessProfile.name);
                }
            );
        }
    };

    const itemsCartLength = cartValues?.length || 0;

    const checkoutItemsParkCard: CheckoutItemParqueType[] = useMemo(() => {
        if (!cartValues?.length) return [] as CheckoutItemParqueType[];

        return cartValues.map(cart => ({
            type: getCartType(cart.type) as CheckoutItemParqueType['type'],
            title: cart.title,
            price: cart.value,
            date: cart.date,
            time: cart.scheduleHour ?? null,
            id: cart.id,
        }));
    }, [cartValues]);

    // const sumValuesCard = useMemo(
    //     () =>
    //         cartValues?.reduce(
    //             (accumulator, item) => accumulator + item.value,
    //             0
    //         ) || 0,
    //     [cartValues]
    // );

    const verifyCheckoutItems = async (): Promise<boolean> => {
        if (Array.isArray(errors.products)) {
            for (const error of errors.products) {
                if (error.name) {
                    toast.warning('Nome inválido. Utilize nome e sobrenome.');
                    return false;
                }
                if (error.document) {
                    toast.warning(
                        'CPF inválido. Utilize um CPF válido ou marque a opção de estrangeiro.'
                    );
                    return false;
                }
            }
        }
        if (!(await trigger())) {
            toast.warning('Preencha todos os campos obrigatórios.');
            return false;
        }
        return true;
    };

    const validateReservationDailyLimits = async (): Promise<boolean> => {
        if (isPaymentLink) return true;
        if (!activeReservation?.token || !cartValues?.length) return true;
        const hasWaves = cartValues.some(v => v.type === CartServiceType.Waves);
        if (!hasWaves) return true;

        const { reservation, error } = await getHospitalityReservationByToken(
            activeReservation.token
        );

        if (error || !reservation) {
            toast.error(error || 'Não foi possível atualizar os limites da reserva.');
            return false;
        }

        setActiveReservation(reservation);

        const reservationUsage = getReservationDailyWaveUsage(reservation);
        const cartUsage = getCartDailyWaveUsage(cartValues);
        const mergedUsage = mergeDailyWaveUsages(reservationUsage, cartUsage);
        const violation = findDailyWaveLimitViolation(mergedUsage);

        if (!violation) return true;

        toast.error(getErrorMessageExact(violation) || getErrorMessage(violation));
        return false;
    };

    const handleSubmitCheckout = async () => {
        if (!token) {
            toast.warning('Você deve fazer login para prosseguir...');
            setIsVisibleAuthModal(true, 'login');
            return;
        }

        if (!address) {
            return toast.error('Preencha o local de faturamento');
        }

        if (!cartValues?.length) return;

        const hasDateRequiringReservation = cartValues.some(item =>
            !canPurchaseStoreDate({
                purchaseDate: item.date,
                activeReservation,
            })
        );

        if (!isPaymentLink && hasDateRequiringReservation) {
            toast.warning(
                activeReservation
                    ? STORE_RESERVATION_WINDOW_MESSAGE
                    : STORE_RESERVATION_REQUIRED_MESSAGE
            );
            return;
        }

        const isFormValid = await verifyCheckoutItems();
        if (!isFormValid) return;

        const hasValidDailyLimit = await validateReservationDailyLimits();
        if (!hasValidDailyLimit) return;

        const itemsTotalValue = cartValues!.reduce((acc, curr) => acc + curr.value, 0);
        const totalValueDecimal = new Decimal(itemsTotalValue);
        
        // Calcular o valor de desconto efetivo
        let effectiveDiscountValue = 0;
        if (coupon) {
            if (coupon.discountValue > 0) {
                effectiveDiscountValue = coupon.discountValue;
            } else if (coupon.percentage && coupon.percentage > 0) {
                effectiveDiscountValue = totalValueDecimal.times(coupon.percentage).dividedBy(100).toNumber();
            }
        }
        const totalDiscountDecimal = new Decimal(effectiveDiscountValue);

        // Logs de depuração removidos
        
        let remainingDiscount = totalDiscountDecimal;

        const debugItemDiscounts: Array<{ index: number; itemPrice: number; itemDiscount: number }> = [];

        const mappedValues = cartValues.map((value, index) => {
            const itemPrice = new Decimal(value.value);
            let itemDiscount = new Decimal(0);

            // Verificar se o cupom se aplica a este item (uso do helper central)
            const couponApplies = coupon ? isCouponApplicableToItem(coupon, value) : true;

            // Debug: removido

            if (coupon && totalDiscountDecimal.greaterThan(0) && couponApplies) {
                if (coupon.percentage && coupon.percentage > 0) {
                    // Lógica para porcentagem: aplicar porcentagem em cada item
                    itemDiscount = itemPrice.times(coupon.percentage).dividedBy(100);
                } else if (coupon.discountValue > 0) {
                    // Lógica para desconto fixo: aplicar sequencialmente (primeiro item consome tudo que der)
                    if (remainingDiscount.greaterThanOrEqualTo(itemPrice)) {
                        // O desconto restante é maior ou igual ao valor do item: desconta tudo do item
                        itemDiscount = itemPrice;
                        remainingDiscount = remainingDiscount.minus(itemPrice);
                    } else {
                        // O desconto restante é menor que o valor do item: aplica o que sobrou
                        itemDiscount = remainingDiscount;
                        remainingDiscount = new Decimal(0);
                    }
                }
            }

            // Garantir que o desconto não exceda o valor do item (para porcentagem)
            if (itemDiscount.greaterThan(itemPrice)) {
                itemDiscount = itemPrice;
            }

            // Armazenar dados de debug por item
            try {
                debugItemDiscounts.push({
                    index,
                    itemPrice: itemPrice.toNumber(),
                    itemDiscount: itemDiscount.toNumber(),
                });
            } catch (e) {
                /* ignore */
            }

            const product: CheckoutProduct = {
                client: {
                    document: value.client!.document,
                    name: value.client!.name,
                },
                date: value.date.toISOString(),
                productId: Number(value.id),
                scheduleId: value.scheduleId,
                giftId: value.giftId,
                discountValue: value.discountValue,
                campaignId: coupon ? undefined : value.campaignId,
                usedCampaignRuleId: coupon ? undefined : value.usedCampaignRuleId,
                value: coupon ? itemPrice.minus(itemDiscount).toNumber() : value.value - (value.discountValue ?? 0),
                freeSession: value.freeSession || undefined,
                reservationId: activeReservation?.id || undefined,
            };

            // Incluir campos de desconto somente se houver desconto aplicado
            if (itemDiscount.greaterThan(0)) {
                product.discountValue = itemDiscount.toNumber();
                product.usedDiscountCoupon = true;
            }

            if (value.giftId) {
                product.giftId = value.giftId;
            }

            return product;
        });

        
        let targetId: null | number = forcedCheckoutId || null;
        let statusId: null | number | undefined = null;

        if (!targetId && !isPaymentLink) {
            const reservationToken = activeReservation?.token;
            const releaseCheckoutLock = reservationToken
                ? acquireCheckoutLock(reservationToken)
                : null;

            if (reservationToken && !releaseCheckoutLock) {
                toast.warning(
                    'Já existe uma finalização em andamento para esta reserva em outra aba.'
                );
                return;
            }

            const payload: CheckoutPayload = {
                couponCode: coupon?.value,
                products: mappedValues,
                address: address,
                
                value: coupon 
                    ? shoppingCartTotalValue - effectiveDiscountValue
                    : shoppingCartTotalValue - sumDiscountValue,
                discountValue: coupon
                    ? effectiveDiscountValue
                    : sumDiscountValue
            };

            if (coupon?.id) {
                payload.discountCouponId = coupon.id;
            }
            // payload will be sent to gateway

            let gatewayResponse: CheckoutResponseDTO | null = null;
            try {
                gatewayResponse = await postCheckout.mutateAsyncCheckout!(payload);
            } catch (err: unknown) {
                const raw = (err instanceof Error ? err.message : 'Erro ao processar o checkout') || 'Erro ao processar o checkout';

                // Se o backend indicar falta do local de faturamento, não mostrar outro toast
                // porque o hook `usePostCheckout` já exibe o aviso amarelo.
                if (raw === 'SalePayment.RequiredAddress') {
                    return; // interrompe o fluxo sem duplicar a notificação
                }

                const friendly = getErrorMessage(raw);
                toast.error(friendly);
                return; // interrompe o fluxo
            } finally {
                releaseCheckoutLock?.();
            }

            if (gatewayResponse && gatewayResponse.saleId !== null) {
                targetId = gatewayResponse.saleId;
                statusId = gatewayResponse.statusId;

                if (activeReservation?.token) {
                    const { reservation: updatedReservation } = await getHospitalityReservationByToken(
                        activeReservation.token
                    );
                    if (updatedReservation) {
                        setActiveReservation(updatedReservation);
                    }
                }
            } else {
                toast.error('Ocorrreu um erro ao reservar o seu carrinho.');
            }
        }

        setCheckoutId(targetId);

        if (targetId && statusId === 4) {
            // Pagamento de 0 reais já foi finalizado
            paymentModal.update({
                isVisible: true,
                method: 'finished',
            });
            toast.success('Voucher resgatado com sucesso!');
            return;
        }

        if (targetId !== null) {
            if (paymentModal.method === 'pix') {
                handleCreatePixPayment(targetId);
            } else if (paymentModal.method === 'card') {
                paymentModal.update({
                    saleId: targetId,
                });

                handleCreateCardPayment(targetId);
            }
        }
    };

    const relatedValuesToCheckoutService: RelatedItemType[] = [];
    const isPendingServices = false;

    // const relatedValuesToCheckoutService: RelatedItemType[] = useMemo(() => {
    //     if (!dataServices || isPendingServices) return [];

    //     return dataServices.map(item => ({
    //         id: String(item.id),
    //         title: item.exhibitionName,
    //         description: item.description,
    //         img: item.image,
    //         icon: MdSurfing,
    //         productTypeName: item.productType.name,
    //         value: item.value,
    //     }));
    // }, [dataServices, isPendingServices]);

    function handleChangePaymentMethod(method: PaymentMethod) {
        trigger();
        switch (method) {
            case null:
                paymentModal.update({
                    isVisible: false,
                    method: null,
                    card: null,
                });
                break;
            case 'pix':
                paymentModal.update({
                    method: 'pix',
                    card: null,
                    installments: null,
                });
                break;
            case 'card':
                paymentModal.update({
                    method: 'card',
                    isVisible: true,
                });
                break;
        }
    }

    useEffect(() => {
        if (cartValues?.length === 0 || !cartValues) {
            navigate('/');
        }
    }, [cartValues, checkoutId, navigate]);

    const handleCreateCardPayment = async (saleId: number) => {
        paymentModal.update({ isPending: true });

        const response = await gatewayService
            .createCardTransaction(
                priceCents,
                cardCredentials!.subMethod!,
                saleId,
                {
                    brand: cardCredentials!.brand,
                    expiry_date: getValidityInput(cardCredentials!.expiration),
                    number: getNCardInput(cardCredentials!.number),
                    security_code: cardCredentials!.cvv,
                },
                cardInstallments ?? 1
            )
            .catch(e => {
                paymentModal.update({ isPending: false });
                return e;
            });

        paymentModal.update({ isPending: false });

        if ('status' in response && response.status === 'COMPLETED') {
            paymentModal.update({
                isVisible: true,
                method: 'finished',
            });
        } else if ('code' in response) {
            gatewayErrorMessages[response.code]
                ? toast.error(
                      `Erro ao processar pagamento: ${gatewayErrorMessages[response.code]}`,
                      {
                          autoClose: false,
                      }
                  )
                : toast.error(
                      `Ocorreu um erro desconhecio ao processar a transação. Por favor, tente novamente mais tarde.`,
                      {
                          autoClose: false,
                      }
                  );
        }
    };

    const checkPaymentStatus = async (transactionId: string) => {
        const responseStatus =
            await gatewayService.findTransaction(transactionId);

        paymentModal.update({
            status: responseStatus.status,
        });

        if (responseStatus.status === 'COMPLETED') {
            paymentModal.update({
                isVisible: true,
                method: 'finished',
            });
        }

        if (responseStatus.status !== 'PENDING') {
            clearInterval(intervalRef.current);
        }
    };

    const handleCreatePixPayment = async (saleId: number) => {
        paymentModal.update({
            isPending: true,
        });

        const response = await gatewayService.createPixTransaction(
            priceCents,
            saleId
        );

        paymentModal.update({
            isPending: false,
        });

        clearInterval(intervalRef.current);

        if ('id' in response) {
            paymentModal.update({
                response: response,
            });

            intervalRef.current = setInterval(() => {
                checkPaymentStatus(response.id);
            }, 1000);

            paymentModal.update({
                isVisible: true,
                method: 'pix',
                saleId: saleId,
            });
        } else if (response.success === false) {
            gatewayErrorMessages[response.code]
                ? toast.error(
                      `Erro ao processar pagamento: ${gatewayErrorMessages[response.code]}`,
                      {
                          autoClose: false,
                      }
                  )
                : toast.error(
                      `Ocorreu um erro desconhecio ao processar a transação. Por favor, tente novamente mais tarde.`,
                      {
                          autoClose: false,
                      }
                  );
            paymentModal.update({
                method: null,
                isVisible: false,
                saleId: null,
                response: null,
                status: null,
                isPending: false,
            });
        }
    };

    const isGiftNoticeVisible = shoppingCartTotalValue === 0;
    const [visibleNoticesVisibleCount, setVisibleNoticesCount] = useState(
        isGiftNoticeVisible ? 2 : 1
    );

    return (
        <>
            <PageContainer>
                <CheckoutContainer>
                    <div className="column">
                        <h3>Itens do carrinho ({itemsCartLength})</h3>

                        <ItemListCard>
                            <WarnNotice
                                variant="yellow"
                                text="No campo de documento, informe o CPF da pessoa que irá surfar."
                                isClosable={true}
                                onHide={() =>
                                    setVisibleNoticesCount(e => e - 1)
                                }
                            />
                            {isGiftNoticeVisible && (
                                <WarnNotice
                                    variant="blue"
                                    text="Você está agendando brindes! A opção de pagamento foi desativada, caso adicione itens pagos, a opção retornará."
                                    isClosable={true}
                                    onHide={() =>
                                        setVisibleNoticesCount(e => e - 1)
                                    }
                                />
                            )}
                            {visibleNoticesVisibleCount > 0 && (
                                <WarnNoticeSeparator />
                            )}

                            {cartValues?.map((item, index) => (
                                <CartSubItem
                                    subItem={{
                                        id: item.idCart!,
                                        date: item.date,
                                        price: coupon
                                            ? item.value
                                            : (item.value - (item.discountValue || 0)),
                                        time: item.scheduleHour || null,
                                        title: item.title,
                                    }}
                                    handleEditTime={() => {
                                        handleOpenCalendar(item.idCart!);
                                    }}
                                    handleRemoveItem={() =>
                                        setCurrentIdRemoving(item.idCart!)
                                    }
                                    isInsideCombo={false}
                                    itemType={item.type}
                                    isLimitedChoices={[2, 7, 10, 12, 13].some(
                                        enumKey =>
                                            userData?.accessProfile.enumKey ===
                                            enumKey
                                    )} // 2 e 7 são os códigos para dependentes e proprietários, que devem ter escolhas limitadas
                                    dependents={dependents || []}
                                    shouldBlockEditing={isEditLocked}
                                    isPaymentLink={isPaymentLink}
                                    key={index}
                                    register={register}
                                    error={errors.products}
                                    index={index}
                                    setValue={setValue}
                                    isForeignerInitialValue={
                                        item.client?.foreigner
                                    }
                                />
                            ))}
                        </ItemListCard>
                    </div>
                    <Summary>
                        <h3>Resumo do carrinho</h3>
                        {/* <CartSummary
                            items={checkoutItemsParkCard}
                            sumCartValues={sumValuesCard}
                        /> */}
                        <CartSummary
                            items={checkoutItemsParkCard}
                            sumCartValues={shoppingCartTotalValue}
                            sumDiscountValue={sumDiscountValue}
                        />
                        <CouponInput />
                        <AddressButton />

                        <PaymentMethodInputs
                            paymentMethod={paymentModal.method}
                            regulation={regulation}
                            isPendingCheckout={
                                postCheckout.isPendingCheckout && !isPaymentLink
                            }
                            handleChangePaymentMethod={
                                handleChangePaymentMethod
                            }
                            handleSubmitCheckout={handleSubmitCheckout}
                            canFinishCheckout={checkoutId !== null}
                            setRegulation={setRegulation}
                            isCortesia={isCortesia}
                        />
                        {!shouldHideRecommendations && (
                            <RelatedItem
                                items={relatedValuesToCheckoutService}
                                isLoading={isPendingServices}
                            />
                        )}
                    </Summary>
                </CheckoutContainer>
                <AnimatePresence>
                    {isCalendarWaveVisible && (
                        <CalendarWithWaves
                            pricesProductId={defaultData.id}
                            onClose={handleModalWaveClose}
                            defaultData={defaultData}
                            key={1}
                        />
                    )}
                    {isCalendarServiceVisible && (
                        <CalendarWithServices
                            pricesProductId={defaultData.id}
                            onClose={handleModalServiceClose}
                            defaultData={defaultData}
                            key={2}
                        />
                    )}
                    {isCalendarParkVisible && (
                        <CalendarWithPark
                            pricesProductId={defaultData.id}
                            onClose={handleModalParkClose}
                            defaultData={defaultData}
                            key={3}
                        />
                    )}
                </AnimatePresence>
            </PageContainer>
        </>
    );
};
